import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect here, let the component handle it
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Questions APIs
export const questionsAPI = {
  getAll: (params) => api.get('/questions', { params }),
  getOne: (questionId) => api.get(`/questions/${questionId}`),
  getRelated: (questionId, limit = 5) => api.get(`/questions/${questionId}/related`, { params: { limit } }),
  create: (data) => api.post('/questions', data),
  delete: (questionId) => api.delete(`/questions/${questionId}`),
};

// Nodes APIs
export const nodesAPI = {
  getByQuestion: (questionId) => api.get(`/nodes/${questionId}`),
  getOne: (nodeId) => api.get(`/node/${nodeId}`),
  create: (data) => api.post('/nodes', data),
  update: (nodeId, data) => api.patch(`/node/${nodeId}`, data),
  delete: (nodeId) => api.delete(`/node/${nodeId}`),
  savePositions: (questionId, positions) => api.post(`/nodes/${questionId}/positions`, { positions }),
};

// Votes APIs
export const votesAPI = {
  vote: (data) => api.post('/vote', data),
  getUserVote: (nodeId) => api.get(`/votes/${nodeId}`),
};

// Comments APIs
export const commentsAPI = {
  getByNode: (nodeId) => api.get(`/comments/${nodeId}`),
  create: (data) => api.post('/comments', data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
};

// User APIs
export const usersAPI = {
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
  searchForMention: (query, questionId) => api.get('/users/search/mention', { 
    params: { q: query, question_id: questionId } 
  }),
};

// Admin APIs
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getQuestions: (params) => api.get('/admin/questions', { params }),
  deleteNode: (nodeId) => api.delete(`/admin/nodes/${nodeId}`),
  getStats: () => api.get('/admin/stats'),
  // Bot management
  getBotStatus: () => api.get('/admin/bot/status'),
  updateBotConfig: (config) => api.post('/admin/bot/config', config),
  startBot: () => api.post('/admin/bot/start'),
  stopBot: () => api.post('/admin/bot/stop'),
  runBotOnce: () => api.post('/admin/bot/run-once'),
  seedContent: (users, mindmaps) => api.post(`/admin/bot/seed?users=${users}&mindmaps=${mindmaps}`),
  deleteBotContent: () => api.delete('/admin/bot/content'),
};

// Gamification APIs
export const gamificationAPI = {
  getAllBadges: () => api.get('/gamification/badges'),
  getUserStats: (userId) => api.get(`/gamification/user/${userId}`),
  getMyStats: () => api.get('/gamification/my-stats'),
  getLeaderboard: (limit = 20) => api.get('/gamification/leaderboard', { params: { limit } }),
};

// Follow & Notifications APIs
export const followAPI = {
  follow: (questionId) => api.post('/follow', { question_id: questionId }),
  checkFollow: (questionId) => api.get(`/follow/${questionId}`),
  getFollowed: () => api.get('/follow'),
};

export const notificationsAPI = {
  getAll: (skip = 0, limit = 20) => api.get('/notifications', { params: { skip, limit } }),
  markRead: (notificationIds) => api.post('/notifications/read', { notification_ids: notificationIds }),
  markAllRead: () => api.post('/notifications/read-all'),
};

export default api;
